import { useTranslation } from "react-i18next";
import { Check, DoorOpen, Eye, EyeOff, Trash2 } from "lucide-react";
import clsx from "clsx";
import { PanelSection } from "../PanelSection";
import { useSectionDisclosure } from "../useSectionDisclosure";
import type { WallListProps } from "./types";

const DRAW =
  "flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md border px-2 text-[13px] font-semibold";

export const WallList = ({
  walls,
  draft,
  areWallsShown,
  didSaveFail = false,
  onToggleShown,
  onDraw,
  onFinish,
  onCancel,
  onRemove,
  isOpen,
  onOpenChange,
}: WallListProps) => {
  const { t } = useTranslation();
  const disclosure = useSectionDisclosure({ isOpen, onOpenChange });

  const openings = walls.filter((wall) => wall.kind === "opening").length;

  return (
    <PanelSection
      testId="plan-walls"
      tone="wall"
      title={t("smartSurveyor.cablesBeta.wallsTitle")}
      count={walls.length - openings}
      isOpen={disclosure.isOpen}
      onToggle={disclosure.toggle}
      action={
        // The layer's own section owns whether the layer is drawn, exactly as
        // the trays' does. A plan traced wall by wall hides the cable otherwise.
        <button
          type="button"
          aria-pressed={areWallsShown}
          onClick={() => onToggleShown(!areWallsShown)}
          title={t(
            areWallsShown
              ? "smartSurveyor.cablesBeta.wallsHide"
              : "smartSurveyor.cablesBeta.wallsShow",
          )}
          className={clsx(
            "mr-1.5 grid h-9 w-9 flex-none place-items-center rounded-md border",
            areWallsShown
              ? "border-slate-300 bg-white text-slate-700"
              : "border-transparent text-slate-400 hover:border-slate-300 hover:bg-white",
          )}
        >
          {areWallsShown ? <Eye size={17} /> : <EyeOff size={17} />}
        </button>
      }
    >
      {/* What a wall IS here, in one line, because it is the only thing on this
          panel that changes the metres without carrying any. */}
      <p className="text-[13px] leading-snug text-slate-600">
        {t("smartSurveyor.cablesBeta.wallsWhat")}
      </p>

      {draft ? (
        // Mid-stroke: the count of corners is the only thing that tells him the
        // plan is listening, and nothing is saved until he finishes.
        <div className="mt-2 rounded-md border border-blue-300 bg-blue-50 p-2">
          <div className="text-[13px] font-semibold text-blue-900">
            {t(
              draft.wallKind === "opening"
                ? "smartSurveyor.cablesBeta.wallsDrawingOpening"
                : "smartSurveyor.cablesBeta.wallsDrawing",
              { count: draft.points.length },
            )}
          </div>
          <div className="mt-1.5 flex gap-1.5">
            <button
              type="button"
              disabled={draft.points.length < 2}
              onClick={onFinish}
              className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md bg-blue-600 px-2 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400"
            >
              <Check size={15} />
              {t("smartSurveyor.cablesBeta.wallsFinish")}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex h-9 flex-none items-center justify-center rounded-md border border-slate-300 bg-white px-2.5 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
            >
              {t("smartSurveyor.cablesBeta.cancel")}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex gap-1.5">
          <button
            type="button"
            onClick={() => onDraw("wall")}
            className={clsx(
              DRAW,
              "border-slate-300 bg-white text-slate-800 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700",
            )}
          >
            {t("smartSurveyor.cablesBeta.wallsDraw")}
          </button>
          {/* A door is not a shorter wall, it is a hole that is already there:
              cable through one is free, and that is what keeps a room he has
              sealed on four sides from costing a Durchbruch. */}
          <button
            type="button"
            onClick={() => onDraw("opening")}
            className={clsx(
              DRAW,
              "border-slate-300 bg-white text-slate-800 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700",
            )}
          >
            <DoorOpen size={15} />
            {t("smartSurveyor.cablesBeta.wallsDrawOpening")}
          </button>
        </div>
      )}

      {/* Only what the server has is in the list, so a failed save leaves the
          wall he drew nowhere at all. Saying nothing would let him price a run
          against a wall the router has never heard of. */}
      {didSaveFail && (
        <p className="mt-2 rounded-md border border-red-300 bg-red-50 px-2 py-1.5 text-[13px] font-medium text-red-800">
          {t("smartSurveyor.cablesBeta.wallsNotSaved")}
        </p>
      )}

      {walls.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {walls.map((wall, index) => (
            <li
              key={wall.id}
              data-testid={`plan-wall-${wall.id}`}
              className="flex items-center gap-2 rounded px-1 py-1 hover:bg-slate-50"
            >
              <span
                aria-hidden
                className={clsx(
                  "h-1.5 w-5 flex-none rounded-sm",
                  wall.kind === "opening"
                    ? "border border-dashed border-stone-500 bg-white"
                    : "bg-stone-700",
                )}
              />
              <span className="min-w-0 flex-1 truncate text-[13px] text-slate-800">
                {t(
                  wall.kind === "opening"
                    ? "smartSurveyor.cablesBeta.wallsOpeningRow"
                    : "smartSurveyor.cablesBeta.wallsWallRow",
                  { number: index + 1, corners: wall.points.length },
                )}
              </span>
              <button
                type="button"
                onClick={() => onRemove(wall.id)}
                title={t("smartSurveyor.cablesBeta.wallsRemove")}
                aria-label={t("smartSurveyor.cablesBeta.wallsRemove")}
                className="grid h-8 w-8 flex-none place-items-center rounded text-slate-400 hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {openings > 0 && (
        <p className="mt-1.5 text-[13px] text-slate-500">
          {t("smartSurveyor.cablesBeta.wallsOpeningCount", { count: openings })}
        </p>
      )}
    </PanelSection>
  );
};
